import { useCallback, useEffect, useRef, useState } from 'react';
import '../css/PageExitConfirmation.css';

const DEFAULT_MESSAGE = 'Your in-progress work will be lost.';

type PendingExit =
  | { type: 'history' }
  | { type: 'link'; destination: URL };

/**
 * Protects builder-style screens whose state only lives in memory.
 *
 * React Router's BrowserRouter cannot block history navigation itself, so a
 * duplicate history entry lets us ask before completing browser Back/swipe.
 */
const usePageExitConfirmation = (message = DEFAULT_MESSAGE) => {
  const markerId = useRef(`page-exit-guard-${Date.now()}-${Math.random()}`);
  const exitApproved = useRef(false);
  const isDirty = useRef(false);
  const [pendingExit, setPendingExit] = useState<PendingExit | null>(null);

  const markSaved = useCallback(() => {
    isDirty.current = false;
  }, []);

  useEffect(() => {
    const marker = { ...(window.history.state || {}), pageExitGuard: markerId.current };
    window.history.pushState(marker, '', window.location.href);

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (exitApproved.current || !isDirty.current) return;
      event.preventDefault();
      event.returnValue = '';
    };

    const markDirtyFromEdit = (event: Event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('.quotation-page, .guided-package-builder')) {
        isDirty.current = true;
      }
    };

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented
        || event.button !== 0
        || event.metaKey
        || event.ctrlKey
        || event.shiftKey
        || event.altKey
      ) return;
      if (!isDirty.current) return;

      const target = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!(target instanceof HTMLAnchorElement) || target.target === '_blank' || target.hasAttribute('download')) return;

      const destination = new URL(target.href, window.location.href);
      const current = new URL(window.location.href);
      if (destination.pathname === current.pathname && destination.search === current.search) return;

      event.preventDefault();
      event.stopPropagation();
      setPendingExit({ type: 'link', destination });
    };

    const handlePopState = () => {
      if (!isDirty.current) {
        window.history.back();
        return;
      }
      setPendingExit({ type: 'history' });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('input', markDirtyFromEdit, true);
    document.addEventListener('change', markDirtyFromEdit, true);
    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('input', markDirtyFromEdit, true);
      document.removeEventListener('change', markDirtyFromEdit, true);
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, []);

  const stayOnPage = useCallback(() => {
    if (pendingExit?.type === 'history') {
      window.history.pushState(
        { ...(window.history.state || {}), pageExitGuard: markerId.current },
        '',
        window.location.href
      );
    }
    setPendingExit(null);
  }, [pendingExit]);

  const leavePage = useCallback(() => {
    if (!pendingExit) return;
    exitApproved.current = true;
    if (pendingExit.type === 'history') {
      setPendingExit(null);
      window.history.back();
      return;
    }

    const { destination } = pendingExit;
    setPendingExit(null);
    window.location.assign(destination.href);
  }, [pendingExit]);

  const confirmation = !pendingExit ? null : (
    <div className="page-exit-confirmation" role="presentation">
      <div className="page-exit-confirmation__backdrop" onClick={stayOnPage} />
      <section className="page-exit-confirmation__dialog" role="alertdialog" aria-modal="true" aria-labelledby="page-exit-confirmation-title" aria-describedby="page-exit-confirmation-message">
        <h2 id="page-exit-confirmation-title">Leave this page?</h2>
        <p id="page-exit-confirmation-message">{message}</p>
        <div className="page-exit-confirmation__actions">
          <button type="button" className="page-exit-confirmation__stay" onClick={stayOnPage}>Stay</button>
          <button type="button" className="page-exit-confirmation__leave" onClick={leavePage}>Leave page</button>
        </div>
      </section>
    </div>
  );

  return { confirmation, markSaved };
};

export default usePageExitConfirmation;
